import { DayPicker, Matcher } from 'react-day-picker';
import { ChangeEvent, FC, FocusEvent, ReactElement, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import { deAT } from 'date-fns/locale';
import i18next from 'i18next';
import { formatDateInput, parseDateInput } from '../util/date-utils';

type DatePickerFieldProps = {
  name: string;
  label: string;
  value: Date | null;
  error?: string;
  touched?: boolean;
  isOpen: boolean;
  disabled?: boolean;
  disabledDays?: Matcher | Matcher[];
  onToggle: () => void;
  onClose: () => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  setFieldValue: (field: string, value: Date | null) => void;
}

const DatePickerField: FC<DatePickerFieldProps> = ({
  name,
  label,
  value,
  error,
  touched,
  isOpen,
  disabled,
  disabledDays,
  onToggle,
  onClose,
  onBlur,
  setFieldValue,
}: DatePickerFieldProps): ReactElement => {
  const [text, setText] = useState<string>(() => formatDateInput(value));
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasInvalidInput, setHasInvalidInput] = useState<boolean>(false);

  useEffect(() => {
    if (!isFocused && !hasInvalidInput) {
      setText(formatDateInput(value));
    }
  }, [value, isFocused, hasInvalidInput]);

  const hasError: boolean = hasInvalidInput || (!!touched && !!error);
  const message: string | undefined = hasInvalidInput ? i18next.t('datePickerField.invalidDate') : error;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const nextText: string = event.target.value;

    setText(nextText);
    setHasInvalidInput(false);
    setFieldValue(name, parseDateInput(nextText));

    if (isOpen) {
      onClose();
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(false);
    setHasInvalidInput(!!text.trim() && !parseDateInput(text));

    onBlur?.(event);
  };

  return (
    <Form.Group controlId={name} className="position-relative">
      <Form.Label>
        {label}
      </Form.Label>

      <InputGroup>
        <Form.Control
          disabled={disabled}
          name={name}
          value={text}
          placeholder={i18next.t('datePickerField.placeholder')}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          isInvalid={hasError}
        />

        <Button
          variant="primary"
          type="button"
          disabled={disabled}
          className="pt-1 pe-3 ps-3 pb-0"
          onClick={onToggle}>
          <i className="fas fa-calendar text-white" />
        </Button>

        <Form.Control.Feedback type="invalid">
          {message}
        </Form.Control.Feedback>
      </InputGroup>

      {isOpen && !disabled && (
        <div className="position-absolute bg-white border rounded shadow-sm mt-1 p-2" style={{ zIndex: 20 }}>
          <DayPicker
            mode="single"
            selected={value ?? undefined}
            locale={deAT}
            disabled={disabledDays}
            onSelect={(day: Date | undefined) => {
              setHasInvalidInput(false);
              setFieldValue(name, day ?? null);
              onClose();
            }}
          />
        </div>
      )}
    </Form.Group>
  )
}

export default DatePickerField;
